import React, { useRef, useEffect, useCallback } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Typography,
	Button,
	Box,
	Grid2,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SignatureCanvas from "react-signature-canvas";
import { processSignatureDataUrl } from "../utility/utils";

const SignatureDialog = ({
	open,
	onClose,
	onSubmit,
	user,
	dataURLToBlob,
	uploadSignature,
}) => {
	const claimantSigRef = useRef(null);
	const containerRef = useRef(null);

	// Resize the signature canvas to match its container so strokes map 1:1 to pixels
	const resizeCanvas = useCallback(() => {
		const canvas = claimantSigRef.current;
		const container = containerRef.current;
		if (!canvas || !container) return;

		const ratio = Math.max(window.devicePixelRatio || 1, 1);
		const canvasEl = canvas.getCanvas();
		const width = container.clientWidth;
		const height = container.clientHeight;

		canvasEl.width = width * ratio;
		canvasEl.height = height * ratio;
		canvasEl.style.width = `${width}px`;
		canvasEl.style.height = `${height}px`;
		canvasEl.getContext("2d").scale(ratio, ratio);
		canvas.clear(); // reset after resize
	}, []);

	useEffect(() => {
		if (!open) return;
		// Wait for dialog to render, then size the canvas
		const timer = setTimeout(resizeCanvas, 100);
		window.addEventListener("resize", resizeCanvas);
		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", resizeCanvas);
		};
	}, [open, resizeCanvas]);

	const clearClaimantSignature = () => claimantSigRef.current.clear();

	const handleSubmit = async () => {
		try {
			if (!claimantSigRef.current?.toDataURL) {
				throw new Error("Signature pad not ready");
			}

			if (claimantSigRef.current.isEmpty()) {
				throw new Error("Please provide a signature before submitting");
			}

			const rawDataUrl = claimantSigRef.current.toDataURL("image/png");
			const processedDataUrl = await processSignatureDataUrl(rawDataUrl, {
				padding: 20,
				targetWidth: 400,
				targetHeight: 150,
				alphaThreshold: 10,
				whiteThreshold: 250,
				background: null,
			});

			const finalDataUrl = processedDataUrl || rawDataUrl;
			const blob =
				typeof dataURLToBlob === "function"
					? dataURLToBlob(finalDataUrl)
					: await (await fetch(finalDataUrl)).blob();

			const formData = new FormData();
			formData.append("signature", blob, "signature.png");
			formData.append("userName", user.userName);
			const response = await uploadSignature(formData);

			if (response?.statusCode === "OK") {
				onSubmit();
			}
		} catch (error) {
			console.error("Error preparing or uploading signature:", error);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle variant="h4" align="center" gutterBottom>
				Signature
				<IconButton
					aria-label="close"
					onClick={onClose}
					style={{ position: "absolute", right: 8, top: 8 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				<Grid2 item size={12} sm={6}>
					<Typography variant="h6" align="center">
						Signature
					</Typography>
					<Box
						ref={containerRef}
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							border: "1px solid #ccc",
							width: "100%",
							height: "200px",
							cursor: "crosshair",
						}}
					>
						<SignatureCanvas
							ref={claimantSigRef}
							penColor="black"
							canvasProps={{
								style: {
									width: "100%",
									height: "100%",
									touchAction: "none",
								},
							}}
						/>
					</Box>
					<Button
						fullWidth
						variant="outlined"
						sx={{ mt: 1 }}
						onClick={clearClaimantSignature}
					>
						Clear
					</Button>
					<Button
						fullWidth
						variant="outlined"
						sx={{ mt: 1 }}
						onClick={handleSubmit}
					>
						Submit
					</Button>
				</Grid2>
			</DialogContent>
		</Dialog>
	);
};

export default SignatureDialog;
